class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.closeCursor = document.querySelector(
      'modal-close-cursor[data-content="#FacetsWrapperDesktop"]'
    );

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    this.facetButton = document.querySelector(".facet-button__show");
    this.productGrid = document.getElementById("ProductGridContainer");
    this.facetForm = this.querySelector("form");
    this.facetSubmitButton = this.querySelector(".facets__submit");

    this.facetsOverlay = this.querySelector(".facets__overlay");
    this.facetWrapper = this.querySelector("#FacetsWrapperDesktop");

    this.facetForm?.addEventListener(
      "input",
      this.debouncedOnSubmit.bind(this)
    );
    this.facetSubmitButton?.addEventListener(
      "click",
      this.debouncedOnSubmit.bind(this)
    );
    // Keep UI classes in sync immediately when inputs change (delegated)
    this.facetForm?.addEventListener(
      "change",
      FacetFiltersForm.updateJsFilterActiveStates
    );

    if (this.classList.contains("facets--desktop")) {
      this.facetWrapper?.addEventListener(
        "keydown",
        this.onHandleKeyDown.bind(this)
      );
      this.facetButton?.addEventListener(
        "click",
        this.handleFacetButtonClick.bind(this)
      );

      this.addEventListener("click", (event) => {
        const btn = event.target.closest(".facets-modal__close");
        if (btn) {
          event.preventDefault();
          this.closeFacets(event);

          if (btn.classList.contains("modal-close-button")) {
            this.facetButton?.focus();
          }
        }
      });

      this.facetsOverlay?.addEventListener(
        "click",
        this.closeFacets.bind(this)
      );
    }

    if (this.querySelector(".facet-sort__select")) {
      this.initSortSelect();
    }

    FacetFiltersForm.initAccessibleFacetCheckboxes();
    FacetFiltersForm.updateJsFilterActiveStates();
    FacetFiltersForm.handleHeroFilters();
    FacetFiltersForm.initHeroFiltersDragScroll();
  }

  static initAccessibleFacetCheckboxes() {
    document.addEventListener("keydown", (e) => {
      if (e.target.closest("label.facet-checkbox")) {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          const input = document.getElementById(e.target.getAttribute("for"));
          if (!input || input.disabled) return;

          input.checked = !input.checked;
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );

    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    document.querySelectorAll(".facets__reset").forEach((btn) => {
      btn.classList.add("disabled");
    });

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    FacetFiltersForm.syncHeroFilterSort();
  }

  // The hero filter row sits outside the AJAX-rendered containers, so its links keep the
  // sort they were server-rendered with. Resync them so navigating a tag keeps the sort.
  static syncHeroFilterSort() {
    const links = document.querySelectorAll(".collection-hero__filters a[href]");
    if (!links.length) return;

    const sortBy = new URLSearchParams(window.location.search).get("sort_by");

    links.forEach((link) => {
      const url = new URL(link.getAttribute("href"), window.location.origin);

      if (sortBy) {
        url.searchParams.set("sort_by", sortBy);
      } else {
        url.searchParams.delete("sort_by");
      }

      link.setAttribute("href", `${url.pathname}${url.search}`);
    });
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        // TODO: uncomment and use later, currently causing problems
        // FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderActiveFilterCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    // TODO: uncomment and use later, currently causing problems
    // FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.renderActiveFilterCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;
    if (
      document.querySelector(".js-load-more") ||
      document.querySelector(".js-infinite-scroll")
    ) {
      initLoadMore();
    }
  }

  static renderProductCount(html) {
    const count =
      new DOMParser()
        .parseFromString(html, "text/html")
        .getElementById("ProductCount")?.innerHTML || "";

    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");

    container.innerHTML = count;
    container.classList.remove("loading");

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderActiveFilterCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetButtonSource = parsedHTML.querySelector(".facet-button__show");
    const facetButtonTarget = document.querySelector(".facet-button__show");

    if (!facetButtonSource || !facetButtonTarget) return;

    const sourceCounter = facetButtonSource.querySelector(
      ".facet-button__show-counter"
    );
    const targetCounter = facetButtonTarget.querySelector(
      ".facet-button__show-counter"
    );

    if (sourceCounter) {
      if (targetCounter) {
        targetCounter.innerHTML = sourceCounter.innerHTML;
      } else {
        const label = facetButtonTarget.querySelector(".facet-button__label");
        if (label)
          label.insertAdjacentElement(
            "afterend",
            sourceCounter.cloneNode(true)
          );
      }
    } else {
      if (targetCounter) targetCounter.remove();
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderQuickFilters(parsedHTML);

    FacetFiltersForm.renderHeroProductsCount(parsedHTML);

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender) {
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest(".js-filter")
      );
    }

    // Update active classes on freshly-inserted filters first
    FacetFiltersForm.updateJsFilterActiveStates();
    FacetFiltersForm.handleHeroFilters();
    FacetFiltersForm.initHeroFiltersDragScroll();

    document.querySelectorAll(".facets__reset").forEach((btn) => {
      btn.classList.remove("disabled");
    });

    FacetFiltersForm.refreshFacetModalFocusTrap();
  }

  static refreshFacetModalFocusTrap() {
    const facetForm = document.querySelector(
      "facet-filters-form.facets--desktop"
    );
    if (!facetForm) return;

    const facetContainer = facetForm.querySelector(".facets__container.active");
    if (!facetContainer) return;

    const dialog = facetForm.querySelector('[role="dialog"]');
    trapFocus(facetForm, dialog || facetForm);
  }

  static renderQuickFilters(parsedHTML) {
    const sourceQuickFilters = parsedHTML.querySelector(
      "#FacetFiltersByProductTypeForm"
    );
    const targetQuickFilters = document.querySelector(
      "#FacetFiltersByProductTypeForm"
    );

    if (!sourceQuickFilters || !targetQuickFilters) return;

    targetQuickFilters.innerHTML = sourceQuickFilters.innerHTML;
  }

  static renderHeroProductsCount(parsedHTML) {
    const source = parsedHTML.querySelector(".collection-hero__count");
    const target = document.querySelector(".collection-hero__count");

    if (!source || !target) return;

    target.textContent = source.textContent;
  }

  static updateJsFilterActiveStates() {
    let anyFilterActive = false;

    document.querySelectorAll(".js-filter").forEach((filter) => {
      // If this filter renders individual list items (.list-menu__item),
      // toggle `active` on each item based on its checkbox state.
      const itemContainers = filter.querySelectorAll(
        ".list-menu__item, .heading-group__filter"
      );

      if (itemContainers.length) {
        // Toggle per-item active class
        itemContainers.forEach((item) => {
          const cb = item.querySelector('input[type="checkbox"]');
          // Checkbox-less items (the "All" button, tag links) get their state from the URL
          if (!cb) return;
          item.classList.toggle("active", cb.checked);
          if (cb.checked) anyFilterActive = true;
        });
        // Don't force the outer .js-filter active here; styles target the item.
        return;
      }

      // For non-list filters (e.g., price ranges), determine active by inputs
      const hasChecked =
        filter.querySelector('input[type="checkbox"]:checked') !== null;
      const hasFilledNumber = Array.from(
        filter.querySelectorAll('input[type="number"]')
      ).some((input) => input.value.trim() !== "");

      const isActive = hasChecked || hasFilledNumber;
      filter.classList.toggle("active", isActive);
      if (isActive) anyFilterActive = true;
    });

    // "All" mirrors its own quick-filter row only — modal facets keep their own state.
    const quickFilterActive =
      document.querySelector(
        '#FacetFiltersByProductTypeForm input[type="checkbox"]:checked'
      ) !== null ||
      document.querySelector(".tag-filters .js-hero-filter.active") !== null;

    document.querySelectorAll(".js-hero-filter-clear").forEach((btn) => {
      btn.classList.toggle("active", !quickFilterActive);
      btn.classList.toggle("disabled", quickFilterActive);
    });

    // Keep clear-all button state in the facets modal in sync with selections.
    document
      .querySelectorAll(".facets-remove__all .facet-remove")
      .forEach((clearWrap) => {
        clearWrap.classList.toggle("active", anyFilterActive);
        clearWrap.classList.toggle("facet-remove--disabled", !anyFilterActive);
      });
  }

  static clearAllFilters() {
    // Uncheck all checkboxes within the product-type quick-filter form
    const checkboxes = document.querySelectorAll(
      '#FacetFiltersByProductTypeForm input[type="checkbox"]'
    );
    checkboxes.forEach((checkbox) => (checkbox.checked = false));

    // Clear number inputs (price ranges)
    document
      .querySelectorAll('#FacetFiltersForm input[type="number"]')
      .forEach((n) => (n.value = ""));

    // Remove active classes from filter containers and hero items
    document
      .querySelectorAll(
        ".js-filter.active, .js-hero-filter.active, .list-menu__item.active"
      )
      .forEach((el) => el.classList.remove("active"));

    // Ensure UI reflects cleared state and fetch full results
    FacetFiltersForm.updateJsFilterActiveStates();
    FacetFiltersForm.renderPage("");
  }

  static renderActiveFacets(html) {
    // Update all active-facets containers (pills shown outside the modal)
    [".active-facets", ".active-facets-desktop"].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML =
        source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  static handleHeroFilters() {
    const checkboxes = document.querySelectorAll(
      '#FacetFiltersByProductTypeForm .js-filter input[type="checkbox"]'
    );

    const tags = document.querySelectorAll(".tag-filters .js-hero-filter");

    const removebtn = document.querySelector(
      "#FacetFiltersByProductTypeForm .js-hero-filter-clear, .collection-hero__filters .js-hero-filter-clear"
    );

    if (!removebtn) return;

    let someSelected = false;

    if (checkboxes?.length) {
      someSelected = Array.from(checkboxes).some(
        (checkbox) => checkbox.checked
      );
    }
    if (tags?.length) {
      const parts = decodeURIComponent(location.pathname)
        .split("/")
        .filter(Boolean);

      const last = parts.at(-1) || "";
      const active = new Set(
        last
          .split("+")
          .filter(Boolean)
          .map((s) => s.toLowerCase())
      );

      tags.forEach((tag) => {
        const handle = (tag.dataset.tagHandle || tag.id || "").toLowerCase();
        tag.classList.toggle("active", active.has(handle));
      });

      Array.from(tags).some(
        (tag) => tag.classList.contains("active") && (someSelected = true)
      );
    }

    if (!removebtn) return;
    if (someSelected) {
      // removebtn.classList.remove("disabled");
      removebtn.classList.remove("active");
    } else {
      // removebtn.classList.add("disabled");
      removebtn.classList.add("active");
    }

    removebtn.addEventListener("click", () => {
      Array.from(checkboxes).forEach((checkbox) => {
        checkbox.checked = false;
      });
      Array.from(tags).forEach((tag) => {
        tag.classList.remove("active");
      });
      removebtn.classList.add("disabled");
    });
  }

  static initHeroFiltersDragScroll() {
    const filtersRows = document.querySelectorAll(".collection-hero__filters");
    if (!filtersRows.length) return;

    filtersRows.forEach((filtersRow) => {
      if (filtersRow.dataset.dragScrollReady === "true") return;

      filtersRow.dataset.dragScrollReady = "true";

      const dragState = {
        isPointerDown: false,
        hasDragged: false,
        startX: 0,
        startScrollLeft: 0,
      };

      let suppressClick = false;

      const stopDrag = () => {
        const shouldSuppressClick = dragState.hasDragged;
        dragState.isPointerDown = false;
        dragState.hasDragged = false;
        filtersRow.classList.remove("is-dragging");

        if (shouldSuppressClick) {
          suppressClick = true;
          setTimeout(() => {
            suppressClick = false;
          }, 0);
        }
      };

      filtersRow.addEventListener("pointerdown", (event) => {
        if (event.pointerType !== "mouse" || event.button !== 0) return;

        dragState.isPointerDown = true;
        dragState.startX = event.clientX;
        dragState.startScrollLeft = filtersRow.scrollLeft;
        dragState.hasDragged = false;

        filtersRow.classList.add("is-dragging");
      });

      filtersRow.addEventListener("pointermove", (event) => {
        if (!dragState.isPointerDown) return;

        const deltaX = event.clientX - dragState.startX;
        if (Math.abs(deltaX) > 4) {
          dragState.hasDragged = true;
        }

        if (!dragState.hasDragged) return;

        event.preventDefault();
        filtersRow.scrollLeft = dragState.startScrollLeft - deltaX;
      });

      filtersRow.addEventListener("pointerup", stopDrag);
      filtersRow.addEventListener("pointercancel", stopDrag);
      filtersRow.addEventListener("pointerleave", () => {
        if (dragState.isPointerDown) stopDrag();
      });

      filtersRow.addEventListener(
        "wheel",
        (event) => {
          if (filtersRow.scrollWidth <= filtersRow.clientWidth) return;

          const scrollDelta = Math.abs(event.deltaX) > 0 ? event.deltaX : event.deltaY;
          if (scrollDelta === 0) return;

          const maxScrollLeft = filtersRow.scrollWidth - filtersRow.clientWidth;
          const nextScrollLeft = Math.min(
            maxScrollLeft,
            Math.max(0, filtersRow.scrollLeft + scrollDelta)
          );

          if (nextScrollLeft === filtersRow.scrollLeft) return;

          event.preventDefault();
          filtersRow.scrollLeft = nextScrollLeft;
        },
        { passive: false }
      );

      filtersRow.addEventListener(
        "click",
        (event) => {
          if (!suppressClick) return;

          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    });
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );

    const forms = [];
    const currentForm = event.target.closest("form");
    // product_type is rendered in both the facets modal and the hero quick-filter row, so
    // only one form may contribute it — otherwise a stale copy resurrects a value the
    // merchant just unchecked. Sorting touches neither form, hence the fallback.
    const productTypeOwner = FacetFiltersForm.getProductTypeOwner(currentForm);

    sortFilterForms.forEach((form) => {
      if (
        form.id === "FacetSortForm" ||
        form.id === "FacetFiltersForm" ||
        form.id === "FacetSortDrawerForm" ||
        form.id === "FacetFiltersByProductTypeForm"
      ) {
        const noJsElements = document.querySelectorAll(".no-js-list");
        noJsElements.forEach((el) => el.remove());
        if (form === productTypeOwner) {
          forms.push(this.createSearchParams(form));
        } else {
          const searchParamsWithoutProductType =
            this.createSearchParams(form)
              ?.split("&")
              ?.filter(
                (element) => !element.startsWith("filter.p.product_type")
              )
              ?.join("&") || "";

          forms.push(searchParamsWithoutProductType);
        }
      }
    });
    this.onSubmitForm(forms.join("&"), event);
  }

  // The form allowed to submit product_type: the one just interacted with when it renders
  // the filter, otherwise any form that does (both are re-synced from the server response,
  // so either is a valid source).
  static getProductTypeOwner(currentForm) {
    if (FacetFiltersForm.hasProductTypeInputs(currentForm)) return currentForm;

    const sources = [
      document.getElementById("FacetFiltersForm"),
      document.getElementById("FacetFiltersByProductTypeForm"),
    ];

    return sources.find((form) => FacetFiltersForm.hasProductTypeInputs(form)) || null;
  }

  static hasProductTypeInputs(form) {
    return Boolean(form?.querySelector('[name^="filter.p.product_type"]'));
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);

    const removebtn = document.querySelector(
      "#FacetFiltersByProductTypeForm .facets-tag-wrapper"
    );
    removebtn?.classList.add("active");
  }

  handleFacetButtonClick(event) {
    event.preventDefault();

    if (!this.facetButton || !this.facetForm) return;

    let labelHide = this.facetButton.querySelector(".hide-label");
    let labelShow = this.facetButton.querySelector(".show-label");
    let container = this.facetForm.querySelector(".facets__container");

    this.facetButton.classList.add("active");
    labelHide?.classList.remove("hidden");
    labelShow?.classList.add("hidden");
    container?.classList.add("active");
    this.closeCursor?.activate();
    document.body.classList.add("overflow-hidden");

    this.addEventListener(
      "transitionend",
      () => {
        trapFocus(this, this.querySelector('[role="dialog"]'));
      },
      { once: true }
    );
  }

  closeFacets(event) {
    event.preventDefault();

    if (!this.facetButton || !this.facetForm) return;

    let labelHide = this.facetButton.querySelector(".hide-label");
    let labelShow = this.facetButton.querySelector(".show-label");
    let container = this.facetForm.querySelector(".facets__container");

    this.facetButton?.classList.remove("active");
    labelHide?.classList.add("hidden");
    labelShow?.classList.remove("hidden");
    container?.classList.remove("active");
    this.closeCursor?.deactivate();

    removeTrapFocus(this.facetButton);
    setTimeout(() => document.body.classList.remove("overflow-hidden"), 300);
  }

  onHandleKeyDown(event) {
    if (
      event.key === "Escape" &&
      this.facetWrapper?.closest(".facets__container.active")
    ) {
      this.closeFacets(event);
    }
  }

  initSortSelect() {
    const nativeSelect = this.querySelector("select.facet-sort-select__input");
    if (!nativeSelect) return;

    nativeSelect.addEventListener("change", () => {
      this.facetForm?.dispatchEvent(new Event("input", { bubbles: true }));
    });
  }

  preventCloseSummaryForHorizontal() {
    if (!this.facetWrapper) return;

    const allDetails = this.facetWrapper.querySelectorAll(
      "#FacetFiltersForm.facets__form--horizontal .js-filter"
    );
    if (!allDetails.length) return;

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (window.innerWidth >= 990) {
          allDetails.forEach((details) => {
            details.setAttribute("open", true);
          });
        }
      });
    });

    observer.observe(this.facetWrapper);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get("facet-filters-form")) {
  customElements.define("facet-filters-form", FacetFiltersForm);
  FacetFiltersForm.setListeners();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
    this.initResetButtons();
  }

  initResetButtons() {
    const minResetBtn = this.querySelector(".field__min-reset-btn");
    const maxResetBtn = this.querySelector(".field__max-reset-btn");

    if (minResetBtn) {
      minResetBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const inputs = this.querySelectorAll(".field__input");
        const minInput = inputs[0];
        const rangeInputs = this.querySelectorAll(".field__range");
        const minRangeInput = rangeInputs[0];

        if (minInput && minRangeInput) {
          const minValue = minInput.getAttribute("min") || "0";
          minInput.value = minValue;
          minRangeInput.value = minValue;
          minRangeInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });
    }

    if (maxResetBtn) {
      maxResetBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const inputs = this.querySelectorAll(".field__input");
        const maxInput = inputs[1];
        const rangeInputs = this.querySelectorAll(".field__range");
        const maxRangeInput = rangeInputs[1];

        if (maxInput && maxRangeInput) {
          const maxValue = maxInput.getAttribute("max") || "";
          maxInput.value = maxValue;
          maxRangeInput.value = maxValue;
          maxRangeInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });
    }
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll(".field__input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") {
      maxInput.setAttribute("min", 0);
    }
    if (maxInput.value === "") {
      minInput.setAttribute("max", maxInput.getAttribute("max"));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  fillSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    inputRangeWrappers.forEach((inputWrapper) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];

      const range = Number(inputRangeTo.max) - Number(inputRangeTo.min);
      const fromCurrent =
        Number(inputRangeFrom.value) - Number(inputRangeTo.min);
      const toCurrent = Number(inputRangeTo.value) - Number(inputRangeTo.min);
      const minRange = (fromCurrent / range) * 100;
      const maxRange = (toCurrent / range) * 100;

      inputWrapper.setAttribute(
        "style",
        `--range-min: ${minRange}%; --range-max: ${maxRange}%`
      );
    });
  }

  controlSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    const inputNumberWrappers = document.querySelectorAll(
      ".facets__price .facets__price-wrapper"
    );

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom =
        inputNumberWrappers[index].querySelectorAll(".field__input")[0];
      const inputNumberTo =
        inputNumberWrappers[index].querySelectorAll(".field__input")[1];

      inputRangeFrom.oninput = () => {
        const from = Number(inputRangeFrom.value);
        const to = Number(inputRangeTo.value);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputNumberFrom.value = from;
        }

        this.fillSlider();
      };

      if (Number(inputRangeTo.value) <= 0) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = Number(inputRangeFrom.value);
        const to = Number(inputRangeTo.value);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }

        this.fillSlider();
      };
    });
  }

  controlInput() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    const inputNumberWrappers = document.querySelectorAll(
      ".facets__price .facets__price-wrapper"
    );

    inputNumberWrappers.forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll(".field__input");
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom =
        inputRangeWrappers[index].querySelectorAll(".field__range")[0];
      const inputRangeTo =
        inputRangeWrappers[index].querySelectorAll(".field__range")[1];

      inputNumberFrom.oninput = () => {
        const from = Number(inputNumberFrom.value);
        const to = Number(inputNumberTo.value);

        if (to && from > to) {
          inputRangeFrom.value = to;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.oninput = () => {
        const from = Number(inputNumberFrom.value);
        const to = Number(inputNumberTo.value);

        if (to >= from) {
          inputRangeTo.value = to;
        } else {
          inputRangeTo.value = from;
        }

        this.fillSlider();
      };

      inputNumberFrom.onblur = () => {
        if (inputNumberFrom.value === "") return;
        const from = Number(inputNumberFrom.value);
        const min = Number(inputNumberFrom.getAttribute("min"));
        const max = Number(inputNumberFrom.getAttribute("max"));

        if (from < min) {
          inputRangeFrom.value = min;
          inputNumberFrom.value = min;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.onblur = () => {
        if (inputNumberTo.value === "") return;
        const to = Number(inputNumberTo.value);
        const min = Number(inputNumberTo.getAttribute("min"));
        const max = Number(inputNumberTo.getAttribute("max"));

        if (to > max) {
          inputRangeTo.value = max;
          inputNumberTo.value = max;
        } else {
          inputRangeTo.value = to;
        }

        this.fillSlider();
      };
    });
  }
}

if (!customElements.get("price-range")) customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");

    // No query params left means nothing to keep — clear everything
    const href =
      event.currentTarget && event.currentTarget.href
        ? event.currentTarget.href
        : this.querySelector("a")?.href || "";
    if (href.indexOf("?") === -1) {
      FacetFiltersForm.clearAllFilters();
      return;
    }

    // Otherwise render exactly what the link keeps (other facets + sort survive)
    form.onActiveFilterClick(event);
  }
}

if (!customElements.get("facet-remove")) customElements.define("facet-remove", FacetRemove);

class ActiveFacetsObserver extends HTMLElement {
  connectedCallback() {
    const topTrigger = document.querySelector(".trigger-top");
    const bottomTrigger = document.querySelector(".trigger-bottom");
    if (!topTrigger || !bottomTrigger) return;

    const topObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add("is-visible");
          } else if (
            !entry.isIntersecting &&
            entry.boundingClientRect.top > 0
          ) {
            this.classList.remove("is-visible");
          }
        });
      },
      { root: null, rootMargin: "0px 0px -100px 0px", threshold: 0 }
    );

    const bottomObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.remove("is-visible");
          } else if (
            !entry.isIntersecting &&
            entry.boundingClientRect.top > window.innerHeight / 2
          ) {
            this.classList.add("is-visible");
          }
        });
      },
      { root: null, rootMargin: "0px 0px -30% 0px", threshold: 0 }
    );

    const media = window.matchMedia("(min-width: 992px)");
    const handleChange = (e) => {
      if (e.matches) {
        topObserver.observe(topTrigger);
        bottomObserver.observe(bottomTrigger);
      } else {
        topObserver.unobserve(topTrigger);
        bottomObserver.unobserve(bottomTrigger);
        this.classList.remove("is-visible");
      }
    };

    handleChange(media);
    media.addEventListener("change", handleChange);
  }
}

if (!customElements.get("active-facets-observer")) {
  customElements.define("active-facets-observer", ActiveFacetsObserver);
}
